import { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import { Loader2, Check, X, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { authAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { CellphoneHint } from '@/components/CellphoneHint';
import { sanitizeCell, isValidSACell } from '@/lib/phone';

const inputClass = "mt-1.5 rounded-xl bg-muted border-border focus:border-vintage focus:ring-1 focus:ring-vintage font-body text-foreground placeholder:text-foreground/40";

function PasswordStrength({ password }) {
  const checks = [
    { label: '8+ characters', ok: password.length >= 8 },
    { label: 'Uppercase (A-Z)', ok: /[A-Z]/.test(password) },
    { label: 'Lowercase (a-z)', ok: /[a-z]/.test(password) },
    { label: 'Number (0-9)', ok: /\d/.test(password) },
    { label: 'Special char (!@#...)', ok: /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(password) },
  ];
  if (!password) return null;
  return (
    <div className="mt-2 space-y-1" data-testid="password-strength">
      {checks.map((c, i) => (
        <div key={i} className="flex items-center gap-2">
          {c.ok ? <Check className="h-3 w-3 text-green-400" /> : <X className="h-3 w-3 text-red-400" />}
          <span className={`font-body text-[11px] ${c.ok ? 'text-green-400' : 'text-foreground/40'}`}>{c.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function AuthPage() {
  const { user, login, register, loading: authLoading, loadSession } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [regForm, setRegForm] = useState({ first_name: '', surname: '', email: '', password: '', cellphone: '' });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const googleBtnRef = useRef(null);

  useEffect(() => {
    if (searchParams.get('error') === 'google_failed') {
      toast.error('Google Sign-In failed. Please try again.');
    }
  }, [searchParams]);

  // Initialize Google Identity Services & render the official button
  useEffect(() => {
    if (user) return;
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      // eslint-disable-next-line no-console
      console.warn('[Google Sign-In] REACT_APP_GOOGLE_CLIENT_ID is not set');
      return;
    }

    let cancelled = false;
    const render = () => {
      if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return false;
      // Idempotency guard: skip if a Google iframe is already mounted in this container.
      if (googleBtnRef.current.querySelector('iframe')) return true;
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            try {
              await authAPI.googleSignIn(response.credential);
              await loadSession();
              toast.success('Signed in with Google');
              navigate('/');
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Google Sign-In failed');
            }
          },
        });
        if (googleBtnRef.current.replaceChildren) {
          googleBtnRef.current.replaceChildren();
        } else {
          googleBtnRef.current.innerHTML = '';
        }
        const containerW = googleBtnRef.current.parentElement?.clientWidth
          || googleBtnRef.current.clientWidth || 320;
        const width = Math.max(240, Math.min(400, containerW));
        // Size the clip wrapper to the button width so the pill hugs it exactly
        // (GIS renders inside an iframe, so we round the corners via the wrapper).
        googleBtnRef.current.style.width = `${width}px`;
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          logo_alignment: 'left',
          width,
        });
        return true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[Google Sign-In] render error:', e);
        return false;
      }
    };

    if (render()) return;
    const interval = setInterval(() => {
      if (render()) clearInterval(interval);
    }, 200);
    const stop = setTimeout(() => clearInterval(interval), 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [user, navigate, loadSession]);

  if (authLoading) return null;
  if (user) return <Navigate to="/" />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await authAPI.forgotPassword(forgotEmail);
    } catch {
      // never reveal whether the email exists
    } finally {
      setForgotSent(true);
      setForgotLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...regForm, terms_accepted: agreedToTerms });
      toast.success('Account created!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const isPasswordValid = regForm.password.length >= 8 &&
    /[A-Z]/.test(regForm.password) &&
    /[a-z]/.test(regForm.password) &&
    /\d/.test(regForm.password) &&
    /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'/`~]/.test(regForm.password);

  return (
    <div data-testid="auth-page" className="max-w-md mx-auto px-4 py-12 sm:py-20">
      <div className="text-center mb-8 sm:mb-10">
        <h1 className="font-heading text-3xl sm:text-4xl font-light text-foreground tracking-tight mb-2">
          Welcome
        </h1>
        <p className="font-body text-sm text-foreground/70">
          Sign in to your account or create a new one
        </p>
      </div>

      <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border">
        {/* Google Sign-In — rendered by Google Identity Services (iframe).
            The wrapper clips it to a pill so it matches the site's rounded buttons. */}
        <div className="w-full flex justify-center mb-2">
          <div
            ref={googleBtnRef}
            data-testid="google-signin-btn"
            className="rounded-full overflow-hidden min-h-[44px] shadow-sm"
          />
        </div>
        <p data-testid="google-terms-notice" className="text-center font-body text-[11px] text-foreground/45 mb-5 leading-relaxed">
          By continuing with Google, you agree to our{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-teal hover:text-teal/80 underline underline-offset-2">
            Terms &amp; Conditions
          </a>
          .
        </p>

        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-border" />
          <span className="font-body text-xs text-foreground/40 uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-muted rounded-xl p-1 mb-5">
            <TabsTrigger
              value="login"
              data-testid="login-tab"
              className="rounded-lg font-body text-sm data-[state=active]:bg-vintage data-[state=active]:text-white"
            >
              Sign In
            </TabsTrigger>
            <TabsTrigger
              value="register"
              data-testid="register-tab"
              className="rounded-lg font-body text-sm data-[state=active]:bg-vintage data-[state=active]:text-white"
            >
              Register
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
              <div>
                <Label className="font-body text-sm text-foreground font-medium">Email</Label>
                <Input
                  data-testid="login-email"
                  type="email"
                  value={loginForm.email}
                  onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))}
                  className={inputClass}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground font-medium">Password</Label>
                <div className="relative">
                  <Input
                    data-testid="login-password"
                    type={showLoginPassword ? 'text' : 'password'}
                    value={loginForm.password}
                    onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
                    className={`${inputClass} pr-10`}
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowLoginPassword(v => !v)}
                    data-testid="login-toggle-password"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground/70"
                  >
                    {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => { setForgotOpen(o => !o); setForgotSent(false); setForgotEmail(loginForm.email); }}
                    data-testid="forgot-password-link"
                    className="font-body text-xs text-teal hover:text-teal/80 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              {forgotOpen && (
                <div
                  data-testid="forgot-password-panel"
                  className="rounded-xl bg-muted/50 border border-border p-4 space-y-3"
                >
                  {forgotSent ? (
                    <div className="flex items-start gap-2" data-testid="forgot-password-sent">
                      <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                      <p className="font-body text-xs text-foreground/80">
                        If an account exists for that email, we&apos;ve sent a password reset link. Please check your inbox (and spam).
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="font-body text-xs text-foreground/70">
                        Enter your email and we&apos;ll send you a reset link.
                      </p>
                      <Input
                        data-testid="forgot-email"
                        type="email"
                        value={forgotEmail}
                        onChange={e => setForgotEmail(e.target.value)}
                        className={inputClass}
                        placeholder="you@example.com"
                      />
                      <Button
                        type="button"
                        onClick={handleForgot}
                        data-testid="forgot-submit"
                        variant="ctaSecondary"
                        disabled={forgotLoading || !forgotEmail}
                        className="w-full rounded-full py-4 font-body text-xs font-semibold disabled:opacity-50"
                      >
                        {forgotLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send reset link'}
                      </Button>
                    </>
                  )}
                </div>
              )}

              <Button
                type="submit"
                data-testid="login-submit"
                variant="ctaPrimary"
                disabled={loading}
                className="w-full rounded-full py-6 font-body text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-md"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="font-body text-sm text-foreground font-medium">First Name</Label>
                  <Input
                    data-testid="register-first-name"
                    value={regForm.first_name}
                    onChange={e => setRegForm(f => ({ ...f, first_name: e.target.value }))}
                    className={inputClass}
                    placeholder="John"
                    required
                  />
                </div>
                <div>
                  <Label className="font-body text-sm text-foreground font-medium">Surname</Label>
                  <Input
                    data-testid="register-surname"
                    value={regForm.surname}
                    onChange={e => setRegForm(f => ({ ...f, surname: e.target.value }))}
                    className={inputClass}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="font-body text-sm text-foreground font-medium">Email</Label>
                <Input
                  data-testid="register-email"
                  type="email"
                  value={regForm.email}
                  onChange={e => setRegForm(f => ({ ...f, email: e.target.value }))}
                  className={inputClass}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground font-medium">Cellphone</Label>
                <Input
                  data-testid="register-cellphone"
                  type="tel"
                  inputMode="tel"
                  maxLength={16}
                  value={regForm.cellphone}
                  onChange={e => setRegForm(f => ({ ...f, cellphone: sanitizeCell(e.target.value) }))}
                  className={inputClass}
                  placeholder="082 123 4567"
                  required
                />
                <CellphoneHint value={regForm.cellphone} />
              </div>
              <div>
                <Label className="font-body text-sm text-foreground font-medium">Password</Label>
                <div className="relative">
                  <Input
                    data-testid="register-password"
                    type={showPassword ? 'text' : 'password'}
                    value={regForm.password}
                    onChange={e => setRegForm(f => ({ ...f, password: e.target.value }))}
                    className={`${inputClass} pr-10`}
                    placeholder="Create a strong password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground/70"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <PasswordStrength password={regForm.password} />
              </div>
              <label
                htmlFor="register-terms"
                className="flex items-start gap-2.5 cursor-pointer select-none"
              >
                <input
                  id="register-terms"
                  data-testid="register-terms-checkbox"
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={e => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-border accent-vintage cursor-pointer"
                />
                <span className="font-body text-xs text-foreground/70 leading-relaxed">
                  I have read and agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="register-terms-link"
                    onClick={e => e.stopPropagation()}
                    className="text-teal font-semibold hover:text-teal/80 underline underline-offset-2"
                  >
                    Terms &amp; Conditions
                  </a>
                  .
                </span>
              </label>
              <Button
                type="submit"
                data-testid="register-submit"
                variant="ctaPrimary"
                disabled={loading || !isPasswordValid || !agreedToTerms || !isValidSACell(regForm.cellphone)}
                className="w-full rounded-full py-6 font-body text-sm font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-md disabled:opacity-50"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Account'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
